<template>
  <div>
    <ReportItemContainer v-if="isReportMode">
      <template #question>{{ question.text }}</template>
      <template #answer>
        <div
          v-for="item in certificates.filter(
            (x) => x.label !== `none of the above`
          )"
          :key="item.label"
          class="mb-3"
        >
          <p>
            <span class="report-item-container__label"
              >{{ getLabel("certificate") }}:
            </span>
            {{ item.label }}
          </p>
          <p>
            <span class="report-item-container__label"
              >{{ getLabel("checked") }}:
            </span>
            {{ getAnswerText(item.checked) }}
          </p>
          <div v-if="item.checked">
            <p>
              <span class="report-item-container__label"
                >{{ getLabel("expiryDate") }}:
              </span>
              {{ item.expiryDate }}
            </p>
            <p>
              <span class="report-item-container__label"
                >{{ getLabel("dateLastAudit") }}:
              </span>
              {{ item.auditDate }}
            </p>
            <p>
              <span class="report-item-container__label"
                >{{ getLabel("certifier") }}:
              </span>
              {{ item.certifier }}
            </p>
          </div>
        </div>
      </template>
    </ReportItemContainer>
    <div v-else>
      <p class="mb-4">
        {{ question.text }}
      </p>
      <div v-for="item in certificates" :key="item.label">
        <v-row>
          <v-col cols="12" sm="6" md="12">
            <div
              v-if="item.label === 'none of the above'"
              class="d-flex align-center"
            >
              <PCheckBox
                :label="item.details"
                :value="item.checked"
                :disabled="isReadOnlyMode"
                @value-changed="(state) => certificateSelected(item, state)"
              />
            </div>
            <div v-else class="d-flex align-center">
              <PCheckBox
                :label="item.details"
                :help-title="item.label"
                :value="item.checked"
                :disabled="isReadOnlyMode"
                @value-changed="(state) => certificateSelected(item, state)"
              />
            </div>
          </v-col>
        </v-row>
        <v-row
          v-if="item.checked && item.label !== 'none of the above'"
          class="mb-0 pb-0"
        >
          <v-col class="pb-0" cols="12" md="6">
            <DateInput
              :label="getLabel('expiryDate')"
              :value="item.expiryDate"
              :disabled="!item.checked"
              @updateEvent="(val) => emitNewExpiryDate(item, val)"
              @valid="(val) => emitValid(val, 'expireDate')"
            />
          </v-col>
          <v-col class="pb-0" cols="12" md="6">
            <DateInput
              :label="getLabel('dateLastAudit')"
              :value="item.auditDate"
              :disabled="!item.checked"
              @updateEvent="(val) => emitNewAuditDate(item, val)"
              @valid="(val) => emitValid(val, 'dateLastAudit')"
            />
          </v-col>
        </v-row>
        <v-row
          v-if="item.checked && item.label !== 'none of the above'"
          class="mt-0"
        >
          <v-col class="pt-0" cols="12">
            <PInputField
              :label="getLabel('certifier')"
              :value="item.certifier"
              :readonly="isReadOnlyMode"
              @updateEvent="(val) => emitNewCertifier(item, val)"
            />
          </v-col>
        </v-row>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, type Ref } from "vue";
import DateInput from "@/components/custom/DateInput.vue";
import ReportItemContainer from "@/components/containers/ReportItemContainer.vue";
import PCheckBox from "@/components/custom/PCheckBox.vue";
import type { QuestionModelDto } from "@/models/dto/questionModelDto";
import { useSelectedQuestionnaire } from "@/stores/selectedQuestionnare";
import { QuestionnaireTemplateCommonItemsId } from "@/models/enum/questionnaireTemplateCommonItemsId";
import type { ItemTextDto } from "@/models/dto/questionDto";
import PInputField from "@/components/custom/PInputField.vue";
import {
  type AnswerCertificateModelV2,
  CertificateModelV2,
} from "@/models/questions/certificateModelV2";

const props = withDefaults(
  defineProps<{
    questionModel: QuestionModelDto;
    answer?: AnswerCertificateModelV2[] | null;
    isReportMode?: boolean;
    isReadOnlyMode?: boolean;
  }>(),
  {
    isReportMode: false,
    isReadOnlyMode: false,
  }
);

const emit = defineEmits<{
  (e: "qEvent", newValue: AnswerCertificateModelV2[]): void;
  (e: "isValid", isValid: boolean, invalidField: string): void;
}>();

const selectedQuestionnaireStore = useSelectedQuestionnaire();
const checkBoxAnswerDisplayText = selectedQuestionnaireStore.getCommonItems(
  QuestionnaireTemplateCommonItemsId.Checkbox
);

const certificateDisplayText = selectedQuestionnaireStore.getCommonItems(
  QuestionnaireTemplateCommonItemsId.Certificates
);

const question = computed(() => props.questionModel.question);
const certificates: Ref<CertificateModelV2[]> = ref(getCertificates());

function getCertificates(): CertificateModelV2[] {
  if (
    question.value.itemsWithDetails == undefined ||
    question.value.itemsWithDetails.length == 0
  ) {
    return new Array<CertificateModelV2>();
  }

  const answersExist = props.answer != undefined && props.answer.length > 0;

  if (!answersExist) {
    return question.value.itemsWithDetails!.map(
      (item: ItemTextDto) => new CertificateModelV2(item.id, item.text)
    );
  }

  return question.value.itemsWithDetails!.map((item: ItemTextDto) => {
    const model = new CertificateModelV2(item.id, item.text);
    const answer = props.answer!.find(
      (a: AnswerCertificateModelV2) => a.label === item.id
    );

    if (answer) {
      model.expiryDate = answer.expiryDate;
      model.auditDate = answer.auditDate;
      model.checked = answer.checked;
      model.certifier = answer.certifier;
    }
    return model;
  });
}

function emitNewCertificates(): void {
  const answerCertificateModels = certificates.value.map((x) =>
    x.toAnswerModel()
  );
  emit("qEvent", answerCertificateModels);
}

function emitValid(isValid: boolean, fieldId: string): void {
  emit("isValid", isValid, `${question.value.id}__${fieldId}`);
}

function certificateSelected(item: CertificateModelV2, state: boolean) {
  toggleNoneOfTheAbove(item);
  item.setChecked(state);
  emitNewCertificates();
}

function getAnswerText(answerId: boolean): string {
  const entry = checkBoxAnswerDisplayText.find(
    (x) => x.id == answerId.toString()
  );
  return entry?.text ?? "";
}

function getLabel(id: string): string {
  return certificateDisplayText.find((x) => x.id == id)?.text ?? "";
}

function toggleNoneOfTheAbove(item: CertificateModelV2) {
  if (item.label == "none of the above") {
    certificates.value.forEach((element) => {
      if (element.label != "none of the above") {
        element.checked = false;
      }
    });
  } else {
    certificates.value.forEach((element) => {
      if (element.label == "none of the above") {
        element.checked = false;
      }
    });
  }
}

const emitNewCertifier = (item: CertificateModelV2, newValue: string) => {
  item.certifier = newValue;
  emitNewCertificates();
};

const emitNewAuditDate = (item: CertificateModelV2, newValue: string) => {
  item.auditDate = newValue;
  emitNewCertificates();
}

const emitNewExpiryDate = (item: CertificateModelV2, newValue: string) => {
  item.expiryDate = newValue;
  emitNewCertificates();
}
</script>
